import type { TranslationObj } from "$/utils/lang"

import type { ISelfDiagnosticQuestionBaseModel, ISelfDiagnosticSummaryBaseModel } from "@/3514/types"

enum EChapterComponentType {
    QuestionGroup = "questionGroup",
    VideoVimeo = "videoVimeo",
    Content = "content",
    AdvancedContent = "advancedContent",
    CoachingMoment = "coachingMomentChat"
}

type _TChapterComponentBaseModel = {
    id: number
    uuid: string
    order: number
    isPublished: boolean
    isRequired: boolean
    type: EChapterComponentType
}

// conversation scenarios --start
interface IChapterConversationScenarioModel {
    id: number
    internalName: string
    description: string
    conversationId?: number | null
    isStarted: boolean
    isCompleted: boolean
    coach: { id: number; name: string; photo: string | null }
}

interface IChapterConversationScenariosComponentModel extends _TChapterComponentBaseModel {
    title: TranslationObj
    conversationScenarios: IChapterConversationScenarioModel[]
}
// conversation scenarios --end

// video --start
interface IChapterVideoComponentModel extends _TChapterComponentBaseModel {
    videoId: string
    title: TranslationObj
}
// video --end

// content --start
interface IChapterContentComponentModel extends _TChapterComponentBaseModel {
    content: TranslationObj
    title: TranslationObj
}
// content --end

// advanced content --start
enum EChapterAdvancedContentComponentAppearanceCount {
    First = "first",
    Second = "second",
    Third = "third"
}

interface IChapterAdvancedContentComponentModel extends _TChapterComponentBaseModel {
    title: TranslationObj | null
    introContent: TranslationObj | null
    featureTitle: TranslationObj
    featureContent: TranslationObj
    outroContent: TranslationObj | null
    appearanceCount: EChapterAdvancedContentComponentAppearanceCount
}
// advanced content --end

// likert --start
interface IChapterLikertComponentQuestionModel extends Omit<ISelfDiagnosticQuestionBaseModel, "isActive"> {
    notApplicablePrompt: TranslationObj | null
}

interface IChapterLikertComponentSummaryModel extends ISelfDiagnosticSummaryBaseModel {
    uuid: string
}

interface IChapterLikertComponentModel extends _TChapterComponentBaseModel {
    answerData?: {
        rank: number
        answerList: {
            isFreeResponseAllowed: boolean
            freeResponsePrompt: string
            isNotApplicable: boolean
            notApplicableLabel: TranslationObj
            optionUuid: string
            optionsCount: number
            questionText: TranslationObj
            questionUuid: string
            rank: number
        }[]
    }
    title: TranslationObj
    questionList: IChapterLikertComponentQuestionModel[]
    summaryList: IChapterLikertComponentSummaryModel[]
    isSummaryAfterCompletionShown: boolean
    isEditableAfterCompletion: boolean
}
// likert --end

// coaching moments --start
interface IChapterCoachingMomentSeedQuestionModel {
    questionUuid: string
    question: string
}

interface IChapterCoachingMomentAnswerDataModel {
    conversationId: number
    seedQuestions: (IChapterCoachingMomentSeedQuestionModel & { answer: string })[]
}

interface IChapterCoachingMomentComponentModel extends _TChapterComponentBaseModel {
    isChatAvailable: boolean
    isChatCompleted: boolean
    previousChatCompletionDate: string | undefined
    previousChatChapterId: number | undefined
    actualSummaryCards: Record<string, string>
    previousSummaryCards: Record<string, string>[]
    seedQuestions: IChapterCoachingMomentSeedQuestionModel[]
    answerData: IChapterCoachingMomentAnswerDataModel
    title: TranslationObj | null
}
// coaching moment --end

type IChapterComponentModel =
    | IChapterVideoComponentModel
    | IChapterContentComponentModel
    | IChapterAdvancedContentComponentModel
    | IChapterLikertComponentModel
    | IChapterConversationScenariosComponentModel
    | IChapterCoachingMomentComponentModel

enum EChapterStatus {
    Open = "open",
    InProgress = "in_progress",
    Completed = "completed"
}

enum EChapterType {
    Access = "access",
    Learn = "learn",
    Prepare = "prepare",
    Reflect = "reflect",
    Practice = "practice"
}

interface IChapterBaseModel {
    components: IChapterComponentModel[]
    enrollmentId: number
    id: number
    moduleTitle: TranslationObj
    nextChapterId: number | null
    phaseOrder: number
    phaseProgressId: number
    previousChapterId: number | null
    status: EChapterStatus
    title: TranslationObj
    uuid: string
}

interface IChapterModel extends IChapterBaseModel {
    type: EChapterType
    // is the only returned field if chapter is not accessible
    redirectChapterId?: number | null
}

export type {
    IChapterContentComponentModel,
    IChapterAdvancedContentComponentModel,
    IChapterLikertComponentModel,
    IChapterBaseModel,
    IChapterModel,
    IChapterLikertComponentQuestionModel,
    IChapterLikertComponentSummaryModel,
    IChapterVideoComponentModel,
    IChapterComponentModel,
    IChapterConversationScenarioModel,
    IChapterConversationScenariosComponentModel,
    IChapterCoachingMomentComponentModel,
    IChapterCoachingMomentSeedQuestionModel,
    IChapterCoachingMomentAnswerDataModel
}
export { EChapterComponentType, EChapterStatus, EChapterAdvancedContentComponentAppearanceCount, EChapterType }
